import { useModal } from '@aloft/toolkit-sui';

import { useEffect } from 'react';

import { useZodForm } from '../../hooks/useZodForm.ts';
import { submitEditPropertyDetailsForm } from '../../redux/features/actions/submitEditPropertyDetailsForm.ts';
import { selectAddress } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectTransactionById } from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { EditPropertyDetailsModalContent } from './internal/EditPropertyDetailsModalContent.tsx';
import { schEditPropertyDetailsForm } from './internal/types.ts';

const editDetailModals = {
  EditDetails: {
    title: 'Edit Details',
    addressLabel: 'Property Address',
    showWarning: true,
    lockAddress: false,
  },
  MLSInfoForSubject: {
    title: 'Edit MLS Listing Info',
    addressLabel: 'Subject Property Address',
    showWarning: false,
    lockAddress: true,
  },
  MLSInfoForComp: {
    title: 'Edit MLS Listing Info',
    addressLabel: 'Comp Address',
    showWarning: true,
    lockAddress: false,
  },
};

type EditDetailModalVariation = keyof typeof editDetailModals;

export function useCraftsmanEditPropertyDetailsModal(
  compIDOrSubject: string,
  editDetailsModalType: EditDetailModalVariation = 'EditDetails',
) {
  const EditPropertyDetailModal = useModal();
  const dispatch = useAppDispatch();
  const address = useAppSelector((state) => selectAddress(state, compIDOrSubject));
  const { mls, mlsListingID } = useAppSelector((state) =>
    selectTransactionById(state, compIDOrSubject),
  ) ?? { mls: undefined, mlsListingID: undefined };

  const { title, addressLabel, showWarning, lockAddress } = editDetailModals[editDetailsModalType];

  const form = useZodForm({
    schema: schEditPropertyDetailsForm,
    defaultValues: {
      address,
      mls,
      mlsListingID,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // Make sure that we properly update our defaultValues if they are changed
  // outside of this particular form (changes could come from the sidebar mls
  // info update or edit details from the triple dots in the property
  // headers). This also fixes the issue of making sure that the proper
  // defaults are set even for the same form, and allowing a reset on close so
  // that when the form is reopened after a cancel it's back to the proper state.
  useEffect(() => {
    form.reset({
      address,
      mls,
      mlsListingID,
    });
  }, [form, address, mls, mlsListingID]);

  const submit = form.handleSubmit(
    (data) => {
      dispatch(submitEditPropertyDetailsForm({ id: compIDOrSubject, ...data }));
    },
    (errPayload) => console.warn(errPayload),
  );

  const handleClose = () => {
    EditPropertyDetailModal.closeModal();
    form.reset();
  };

  return {
    modal: (
      <EditPropertyDetailModal.Modal
        size="xl"
        isOpen={EditPropertyDetailModal.isModalOpen}
        onClose={handleClose}
        closeOnEsc
        title={title}
        primaryAction={{
          id: 'edit-comp-details-btn',
          text: 'Save',
          onClick: () => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            submit();
            handleClose();
          },
        }}
        secondaryAction={{
          text: 'Cancel',
          onClick: handleClose,
        }}
      >
        <EditPropertyDetailsModalContent
          form={form}
          addressLabel={addressLabel}
          showWarning={showWarning}
          lockAddress={lockAddress}
        />
      </EditPropertyDetailModal.Modal>
    ),
    props: EditPropertyDetailModal,
  };
}
